/**
 * useInstancePush — subscribe to a specific instance's live updates.
 *
 * Shared between ora and the Outlook add-in. Parameterized over the
 * app-specific transport: `getStatus` (Remix fetcher in ora,
 * orchestratorApi.getStatus in the add-in), `getPushChannel` (negotiate
 * descriptor resolver), and an optional `joinSignalRGroup` (emulator-only
 * explicit group join — production Azure SignalR Service honours JWT
 * `groups` automatically).
 *
 * Per plan §"Stage 2 — In-flight: live status":
 *   * SignalR push (workflowEvent) is the primary transport when the
 *     instance is in an active state (running / paused_hitl). Events fold
 *     directly into local state for snappy UI; status_changed events
 *     trigger a refetch so phases / inputs / metadata stay current
 *     (those fields aren't carried in the push payload).
 *   * Adaptive polling backstops the push channel:
 *       - SignalR unavailable → fast cadence (2s visible, 30s hidden)
 *       - SignalR connected   → heartbeat cadence (60s) — push drives most
 *                                updates, polling reconciles drift and
 *                                catches silently-dropped events.
 *     Polling is paused on hidden tabs and stops once a terminal status is
 *     observed.
 *   * No connection is opened for terminal instances (the polling loop
 *     short-circuits the same way) — saves the negotiate round-trip when a
 *     completed instance is reopened from history.
 *
 * Generic over `T extends InstanceStatusSnapshot` so consumers can keep
 * type information about their richer status shapes (ora's
 * WorkflowStatusResponse, the add-in's InstanceStatus) without casting.
 *
 * Returns `{ status, currentPhase, lastError, lastSuccessAt, isStale,
 * isTerminal, isLiveConnected, isSignalRUnavailable, refetch, subscribe }`.
 * Per-phase STATUS is not returned: callers derive it with
 * `derivePhaseStatusesFromInstance`, which reads the `phases` history off
 * `status` directly.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  InstanceStatusSnapshot,
  WorkflowEvent,
  PushChannelDescriptor,
} from "./types";
import { ANALYSIS_PROGRESS_EVENT } from "./analysisProgress";
import { useSignalR, WORKFLOW_EVENT_METHOD } from "./signalr/useSignalR";
import { isTerminalDrawerStatus } from "./sectionOrder";
import { applyEventToStatus, mergeStatus } from "./statusMerge";

export interface UseInstancePushOptions<
  T extends InstanceStatusSnapshot = InstanceStatusSnapshot,
> {
  /** Fetch the current status envelope. Wraps Remix fetcher in ora,
   *  orchestratorApi.getStatus in the add-in. Required. */
  getStatus: (instanceId: string) => Promise<T>;
  /** Resolve the push-channel negotiate descriptor. Forwarded to useSignalR. */
  getPushChannel: (instanceId: string) => Promise<PushChannelDescriptor>;
  /** Optional explicit group-join (local emulator only). When omitted, no
   *  join call is made — production Azure SignalR Service honours JWT
   *  groups automatically. */
  joinSignalRGroup?: (
    instanceId: string,
    connectionId: string,
    hub: string,
  ) => Promise<void>;
  /** Whether to actively subscribe — drawer in `live` mode passes true. */
  enabled?: boolean;
  /** Visible cadence used when SignalR is unavailable / not yet connected.
   *  Default 2000ms. */
  visibleIntervalMs?: number;
  /** Hidden cadence used when SignalR is unavailable / not yet connected.
   *  Default 30000ms. */
  hiddenIntervalMs?: number;
  /** Safety-heartbeat cadence used when SignalR is connected. Catches the
   *  failure mode where the SignalR Service silently drops a workflowEvent
   *  (or the substrate never emits one) WITHOUT disconnecting the WebSocket —
   *  i.e. the connection holds but no `wf_event` arrives. At the old 60s value
   *  an open drawer could sit on stale state for up to a minute, which read as
   *  "nothing updates until I close and reopen". 10s keeps the drawer current
   *  without a reload while push (when healthy) still drives instant updates.
   *  Default 10000ms. */
  heartbeatIntervalMs?: number;
  /** Threshold beyond `lastSuccessAt` that flips `isStale` true. */
  staleAfterMs?: number;
  /** Disable side effects in tests. */
  disabled?: boolean;
}

export interface UseInstancePushResult<
  T extends InstanceStatusSnapshot = InstanceStatusSnapshot,
> {
  status: T | null;
  currentPhase: string | null;
  lastError: Error | null;
  lastSuccessAt: number | null;
  isStale: boolean;
  isTerminal: boolean;
  loading: boolean;
  /** True when a SignalR connection is open for this instance. */
  isLiveConnected: boolean;
  /**
   * True after the substrate signaled SignalR is unavailable (503 on negotiate
   * or repeated connect failures). Sticky for the session — once tripped, the
   * polling loop runs at the fast cadence and the UI surfaces a "polling-only"
   * indicator until the page is reloaded.
   */
  isSignalRUnavailable: boolean;
  /**
   * Refetch the current status envelope. Pass `{ authoritative: true }` to
   * skip mergeStatus and replace local state with the server response —
   * use after a user action (cancel/resume) where the server's response
   * is the truth and the merge's HITL-beats-non-HITL race protection
   * would otherwise hold a stale warm_hitl/paused_hitl panel up.
   *
   * Resolves `true` only when the HTTP fetch succeeded and state was applied;
   * resolves `false` on a failed fetch, a missing instanceId, or an unmount.
   */
  refetch: (options?: { authoritative?: boolean }) => Promise<boolean>;
  /** Subscribe to any SignalR event on the current instance's hub connection. */
  subscribe: <P = unknown>(event: string, handler: (payload: P) => void) => () => void;
}

export function useInstancePush<
  T extends InstanceStatusSnapshot = InstanceStatusSnapshot,
>(
  instanceId: string | null | undefined,
  options: UseInstancePushOptions<T>,
): UseInstancePushResult<T> {
  const {
    getStatus,
    getPushChannel,
    joinSignalRGroup,
    enabled = true,
    visibleIntervalMs = 2_000,
    hiddenIntervalMs = 30_000,
    heartbeatIntervalMs = 10_000,
    staleAfterMs = 30_000,
    disabled = false,
  } = options;

  // Hold the parameterized deps in refs so callers don't have to memoize
  // them. Without this, an unmemoized arrow function changes identity every
  // render → fetchOnce's useCallback re-creates → the polling effect (which
  // has fetchOnce in its deps) tears down and rebuilds its timer every
  // render. Mirror useSignalR.ts's pattern for onReconnected/onConnected.
  const getStatusRef = useRef(getStatus);
  getStatusRef.current = getStatus;
  const joinSignalRGroupRef = useRef(joinSignalRGroup);
  joinSignalRGroupRef.current = joinSignalRGroup;

  const [status, setStatus] = useState<T | null>(null);
  const [lastError, setLastError] = useState<Error | null>(null);
  const [lastSuccessAt, setLastSuccessAt] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState<number>(Date.now());

  const mountedRef = useRef(true);
  // Always-fresh handle to the latest status — read by the polling loop
  // so terminal-state detection sees the value the most recent fetchOnce
  // wrote, not the one captured by the effect's initial closure.
  const statusRef = useRef<T | null>(null);
  // Tracks whether the polling effect has fired its initial fetch for the
  // current instanceId. Reset when instanceId changes so a new instance
  // does its own initial fetch.
  const hasFetchedRef = useRef(false);
  // The instance the hook is pointed at RIGHT NOW, readable from async
  // continuations that were created for an earlier one. Everything that
  // writes instance-shaped state compares against this before applying.
  const currentInstanceIdRef = useRef<string | null>(instanceId ?? null);
  currentInstanceIdRef.current = instanceId ?? null;

  // Reset ALL per-instance state whenever the consumer changes instanceId.
  // Resetting only the fetch guard isn't enough — `statusRef.current` and
  // `status` persist across instance switches by default, and mergeStatus
  // would then compare the NEW instance's fetched state against the PREVIOUS
  // instance's lingering snapshot. When the prior instance was at a HITL
  // gate (recent updated_at) and the new instance is in a long-running step
  // (older updated_at), the merge would let the old paused_hitl win and the
  // drawer would show stale state for the new instance until something
  // newer arrived (SignalR push, or a state-changing poll). Without SignalR
  // that "something newer" might never come, leaving the drawer permanently
  // stuck on the wrong instance's state.
  //
  // Done DURING RENDER (React's documented "reset state when a prop changes"
  // pattern) rather than in an effect. An effect-based reset always commits
  // one frame in which the NEW instanceId is paired with the PREVIOUS
  // instance's status — a frame the drawer paints, and worse, a frame from
  // which anything reading both values draws a false conclusion about the
  // new instance.
  const [renderedInstanceId, setRenderedInstanceId] = useState<string | null>(
    instanceId ?? null,
  );
  if ((instanceId ?? null) !== renderedInstanceId) {
    setRenderedInstanceId(instanceId ?? null);
    setStatus(null);
    setLastError(null);
    setLastSuccessAt(null);
    statusRef.current = null;
    hasFetchedRef.current = false;
  }

  // Computed early so it can be referenced in effect deps arrays below.
  const isTerminal =
    !!status && isTerminalDrawerStatus((status.status ?? "").toLowerCase());
  // Sticky set of instanceIds confirmed terminal this mount cycle. Once an
  // instance is terminal it never goes back, so we track it here to survive
  // React StrictMode's simulated unmount+remount which temporarily resets
  // `status` to null and would otherwise flip `isTerminal` false,
  // re-enabling the connect effect.
  const terminalInstancesRef = useRef<Set<string>>(new Set());
  // Recorded in an EFFECT, never during render. A render-phase add pairs
  // whatever `status` holds with whatever `instanceId` the consumer has just
  // switched to, and during a switch those describe two different workflows:
  // the OLD instance's terminal status would mark the NEW instance terminal
  // for the rest of the mount, and the connect effect below would then skip
  // SignalR for it permanently — a live workflow with no push and (once the
  // poll loop also sees terminal) no poll either. Effects observe committed
  // state only, which the reset above has already cleared.
  useEffect(() => {
    if (isTerminal && instanceId) terminalInstancesRef.current.add(instanceId);
  }, [isTerminal, instanceId]);

  const fetchOnce = useCallback(async (authoritative = false): Promise<boolean> => {
    if (!instanceId) return false;
    // The instance THIS call belongs to, captured before the await. A
    // `refetch` closure outlives the instance it was created for — the
    // drawer's post-action burst ladder keeps firing for ~20s after a
    // cancel, across any number of row switches — and the await alone is
    // long enough for the consumer to move on. Without the guard below, a
    // late response for the PREVIOUS instance lands in the CURRENT
    // instance's state and then LATCHES there: a terminal status beats
    // non-terminal in mergeStatus, `authoritative` skips the merge outright,
    // schedule() stops polling the moment it sees terminal, and the sticky
    // terminal set stops SignalR. The drawer ends up frozen on another
    // workflow's outcome until it is closed and reopened.
    const forInstanceId = instanceId;
    setLoading(true);
    try {
      const next = await getStatusRef.current(forInstanceId);
      if (!mountedRef.current) return false; // fetch succeeded but component unmounted — state update would be moot
      if (forInstanceId !== currentInstanceIdRef.current) {
        console.info("[useInstancePush] discarding status for a stale instance", {
          fetched_for: forInstanceId,
          current_instance: currentInstanceIdRef.current,
        });
        return false;
      }
      // Merge monotonically so a lagging HTTP response never regresses step_index
      // past a more-advanced SignalR push. HTTP derives step_index from current_phase
      // (last completed phase, one behind the HITL gate); SignalR carries the HITL
      // gate's index from current_step_index. mergeStatus picks the max.
      //
      // `authoritative=true` skips the merge and replaces local state with the
      // server response verbatim. Use ONLY for refetches that follow a user
      // action (cancel/resume) — the user just told the substrate to advance,
      // so the server's response IS the truth and the merge's
      // HITL-beats-non-HITL race protection would otherwise hold the stale
      // warm_hitl panel up indefinitely after a successful resume.
      //
      // Update statusRef BEFORE setStatus so the polling loop's next schedule()
      // (called synchronously after fetchOnce in .then()) sees the new status.
      // React's updater function runs at commit time, not at setState call time —
      // so doing the ref update inside setStatus((prev) => ...) leaves a window
      // where statusRef.current is stale and schedule() can't detect a terminal
      // status.
      const prevSnapshot = statusRef.current as InstanceStatusSnapshot | null;
      // `authoritative=true` is used by burst-poll refetches after a user
      // action (cancel / resume / HITL approve). The intent is to bypass
      // mergeStatus's HITL-beats-non-HITL race protection so the panel
      // doesn't stay stuck on a stale warm_hitl when the user has already
      // resumed. But it MUST still preserve step_index / total_steps
      // monotonicity — HTTP frequently returns an older step_index than
      // the most-recent SignalR push (HTTP persists the current_step_index
      // of the HITL gate; SignalR advances on each step_started/step_completed),
      // and a full overwrite would visually regress the pipeline counter
      // after every Approve click.
      const authoritativeNext = authoritative
        ? ({
            ...(next as InstanceStatusSnapshot),
            step_index:
              (next as InstanceStatusSnapshot).step_index != null
                ? Math.max(
                    prevSnapshot?.step_index ?? -1,
                    (next as InstanceStatusSnapshot).step_index ?? -1,
                  )
                : (prevSnapshot?.step_index ?? null),
            total_steps:
              (next as InstanceStatusSnapshot).total_steps != null
                ? Math.max(
                    prevSnapshot?.total_steps ?? 0,
                    (next as InstanceStatusSnapshot).total_steps ?? 0,
                  )
                : (prevSnapshot?.total_steps ?? null),
          } as T)
        : null;
      const merged = authoritative
        ? (authoritativeNext as T)
        : (mergeStatus(
            prevSnapshot,
            next as InstanceStatusSnapshot,
          ) as T);
      statusRef.current = merged;
      setStatus(merged);
      setLastSuccessAt(Date.now());
      setLastError(null);
      return true;
    } catch (err) {
      if (!mountedRef.current) return false;
      const e = err instanceof Error ? err : new Error(String(err));
      if (forInstanceId !== currentInstanceIdRef.current) {
        console.info("[useInstancePush] ignoring poll failure for a stale instance", {
          fetched_for: forInstanceId,
          current_instance: currentInstanceIdRef.current,
          error: e.message,
        });
        return false;
      }
      setLastError(e);
      console.warn("useInstancePush: poll failed", {
        instance_id: forInstanceId,
        error: e.message,
      });
      return false;
    } finally {
      // Deliberately NOT instance-guarded: this call set `loading` true while
      // it was still the current instance, so it owes the flag a reset even
      // when its result is discarded. Guarding it would strand `loading` true.
      if (mountedRef.current) setLoading(false);
    }
  }, [instanceId]);

  const refetch = useCallback(
    async (options?: { authoritative?: boolean }): Promise<boolean> => {
      setLastError(null);
      return await fetchOnce(options?.authoritative ?? false);
    },
    [fetchOnce],
  );

  // SignalR — single useSignalR() per hook instance. The `isConnected` flag
  // gates the polling loop's cadence (60s heartbeat when connected, 2s fast
  // when not). `onReconnected` reconciles missed events; `onConnected`
  // performs the optional group-join AND fires fetchOnce immediately after
  // the join resolves to close the connect→join race window.
  const signalR = useSignalR(getPushChannel, {
    onReconnected: (id) => {
      if (id !== instanceId) return;
      console.info(
        "[useInstancePush] SignalR reconnected — refetching to reconcile missed events",
        { instance_id: id },
      );
      void fetchOnce();
    },
    onConnected: async ({ instanceId: id, connectionId, hub }) => {
      if (!connectionId) return;
      const join = joinSignalRGroupRef.current;
      if (!join) return;
      // Skip group join for terminal instances — the connection is about to be
      // torn down by the post-connect guard anyway.
      if (isTerminalDrawerStatus((statusRef.current?.status ?? "").toLowerCase())) return;
      try {
        await join(id, connectionId, hub);
        // Backfill any state change that fired during the join window —
        // the substrate doesn't replay workflowEvents that landed before
        // we were in the group.
        if (id === instanceId) {
          void fetchOnce();
        }
      } catch (err) {
        console.warn("[useInstancePush] joinSignalRGroup failed", {
          instance_id: id,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    },
  });

  // ── SignalR subscribe lifecycle ─────────────────────────────────────
  // Connect once per (instanceId, enabled, isUnavailable, isTerminal).
  // `isTerminal` is in the dep array so the effect re-runs and returns early
  // the moment a terminal status is observed — status transitions that don't
  // reach terminal don't change isTerminal and don't retrigger the effect.
  // The cleanup here still calls disconnect on unmount/dep-change; a connect()
  // still in flight then is released by useSignalR when it lands, and the
  // `cancelled` guard only skips subscribing.
  useEffect(() => {
    if (!enabled || disabled || !instanceId) return;
    if (signalR.isSignalRUnavailable) {
      console.info(
        "[useInstancePush] SignalR skip — substrate marked unavailable; polling at fast cadence",
        { instance_id: instanceId },
      );
      return;
    }
    // Don't open a connection for an already-terminal instance — saves the
    // negotiate round-trip when a completed/failed instance is reopened.
    // Check both the closure value AND the sticky ref: the ref survives a
    // StrictMode-induced status reset that would briefly flip isTerminal false.
    if (isTerminal || terminalInstancesRef.current.has(instanceId)) return;

    let cancelled = false;
    let unsubscribe: (() => void) | null = null;

    console.info("[useInstancePush] SignalR connect: starting", { instance_id: instanceId });
    void (async () => {
      try {
        await signalR.connect(instanceId);
      } catch (err) {
        console.warn("[useInstancePush] SignalR connect: failed", {
          instance_id: instanceId,
          error: err instanceof Error ? err.message : String(err),
        });
        return;
      }
      // Cleaned up while connect was in flight: useSignalR released that acquire
      // when it landed, so there is nothing to close and nothing to subscribe.
      if (cancelled) return;
      // Guard: the initial poll may have fetched a terminal status while the
      // negotiate round-trip was in-flight. The terminal-disconnect effect fired
      // then but couldn't close the connection (it wasn't in connectionsRef yet).
      // Disconnect now so we don't hold a live WS for a finished workflow.
      if (isTerminalDrawerStatus((statusRef.current?.status ?? "").toLowerCase())) {
        void signalR.disconnect(instanceId);
        return;
      }
      console.info("[useInstancePush] SignalR connect: succeeded", { instance_id: instanceId });

      // Fold partial fields immediately for snappy UI; refetch on
      // status_changed to pull fields the event omits (phases,
      // inputs, metadata). Step events arrive with partials only — those
      // are surface-only updates so we skip the refetch to avoid hammering.
      unsubscribe = signalR.subscribe<WorkflowEvent>(WORKFLOW_EVENT_METHOD, (evt) => {
        if (!evt || !evt.instance_id || evt.instance_id !== instanceId) return;
        // A running analysis's live progress line is not a status change.
        if (evt.event_type === ANALYSIS_PROGRESS_EVENT) return;
        const prev = statusRef.current;
        if (!prev) {
          if (evt.event_type === "status_changed") void fetchOnce();
          return;
        }
        // ONE fold, ONE contract. This site used to carry an inlined
        // duplicate of applyEventToStatus that never copied `evt.step_id` and
        // set no staleness marker — so on the REAL push channel (both hosts
        // subscribe here) the displayed milestone froze between refetches even
        // though the canonical fold was correct. It now delegates, and keeps
        // only the add-in's `error_message` alias on top: applyEventToStatus
        // owns `error`, and the add-in's InstanceStatus reads `error_message`.
        const merged: T = {
          ...(applyEventToStatus(prev, evt) as T),
          error_message: evt.error ?? prev.error_message ?? null,
        } as T;
        statusRef.current = merged;
        setStatus(merged);
        setLastSuccessAt(Date.now());

        // Refresh phases / inputs / metadata on lifecycle transitions —
        // those fields drive PhasePipeline + LiveBody body sections.
        if (evt.event_type === "status_changed") void fetchOnce();
      });
    })();

    return () => {
      cancelled = true;
      try {
        unsubscribe?.();
      } catch {
        // best-effort cleanup
      }
      void signalR.disconnect(instanceId);
    };
  }, [instanceId, enabled, disabled, signalR.isSignalRUnavailable, isTerminal]);

  // (The per-instance reset lives at the top of the hook and runs during
  // render — see `renderedInstanceId`. It used to be an effect here, which
  // always committed one frame of new-id-with-old-status first.)

  // ── Polling lifecycle ───────────────────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;

    if (!enabled || disabled || !instanceId) {
      // Not subscribed — clear state so a re-enable starts fresh.
      return () => {
        mountedRef.current = false;
      };
    }

    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const isHidden = (): boolean =>
      typeof document !== "undefined" &&
      document.visibilityState === "hidden";

    const isTerminalNow = (): boolean => {
      const tok = (statusRef.current?.status ?? "").toLowerCase();
      return isTerminalDrawerStatus(tok);
    };

    // Cadence matrix:
    //   hidden tab                       → hiddenIntervalMs (30s)
    //   terminal status                  → stop entirely
    //   SignalR connected (healthy push) → heartbeatIntervalMs (10s)
    //                                      Catches the case where the WS holds
    //                                      but no wf_event arrives (silent push
    //                                      drop, or substrate not emitting) —
    //                                      keeps the drawer current without a
    //                                      reload. Push still drives instant
    //                                      updates when it's working.
    //   SignalR unavailable              → visibleIntervalMs (2s)
    //   SignalR connecting / not ready   → visibleIntervalMs (2s)
    const cadence = (): number => {
      const tok = (statusRef.current?.status ?? "").toLowerCase();
      // pending_edocs / pending_email_send are active (non-terminal) but COLD —
      // released by the daily eDoc cron or by the AM sending the reply, never by
      // anything a fast cadence would catch. Poll at 60s regardless of SignalR
      // state (connected OR not connected), so the drawer reconciles drift
      // without hammering during a multi-day wait.
      if (tok === "pending_edocs" || tok === "pending_email_send") {
        return 60_000;
      }
      if (isHidden()) return hiddenIntervalMs;
      if (signalR.isConnected && !signalR.isSignalRUnavailable) {
        return heartbeatIntervalMs;
      }
      return visibleIntervalMs;
    };

    const cadenceReason = (): string => {
      const tok = (statusRef.current?.status ?? "").toLowerCase();
      if (tok === "pending_edocs" || tok === "pending_email_send") {
        return "cold wait (pending_edocs/pending_email_send) — 60s reconcile, any SignalR state";
      }
      if (isHidden()) return "tab hidden";
      if (signalR.isConnected && !signalR.isSignalRUnavailable) {
        return "SignalR connected — safety heartbeat (catches silent push drops)";
      }
      if (signalR.isSignalRUnavailable) return "SignalR unavailable — fast polling";
      return "SignalR not yet connected — fast polling";
    };

    const schedule = () => {
      if (cancelled) return;
      // Stop polling once we've observed a terminal state — substrate
      // never flips back, so we can release the timer until the parent
      // reopens the drawer (which will remount this hook).
      if (isTerminalNow()) {
        console.info("[useInstancePush] poll stopped — terminal status", {
          instance_id: instanceId,
          status: statusRef.current?.status,
        });
        return;
      }
      const ms = cadence();
      console.info("[useInstancePush] poll scheduled", {
        instance_id: instanceId,
        in_ms: ms,
        reason: cadenceReason(),
        signalr_connected: signalR.isConnected,
        signalr_unavailable: signalR.isSignalRUnavailable,
      });
      timer = setTimeout(async () => {
        await fetchOnce();
        schedule();
      }, ms);
    };

    // Initial fetch only on the polling effect's first run for this
    // instanceId. Subsequent re-runs (e.g. signalR.isConnected flipping
    // true once the WebSocket handshake completes) just reschedule with
    // the new cadence — no need to refetch on every dep change.
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      void fetchOnce().then(schedule);
    } else {
      schedule();
    }

    const onVisibility = () => {
      if (!isHidden()) {
        if (timer) clearTimeout(timer);
        void fetchOnce();
        schedule();
      } else if (timer) {
        clearTimeout(timer);
        schedule();
      }
    };

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }

    return () => {
      cancelled = true;
      mountedRef.current = false;
      if (timer) clearTimeout(timer);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    instanceId,
    enabled,
    disabled,
    visibleIntervalMs,
    hiddenIntervalMs,
    heartbeatIntervalMs,
    fetchOnce,
    signalR.isConnected,
    signalR.isSignalRUnavailable,
  ]);

  // Close the WebSocket as soon as a terminal status is observed.
  // Polling already stops at this point (schedule() returns early); keeping
  // the WS open would waste a SignalR connection slot for no benefit.
  useEffect(() => {
    if (!instanceId || !status) return;
    if (!isTerminalDrawerStatus((status.status ?? "").toLowerCase())) return;
    console.info("[useInstancePush] terminal — closing SignalR connection", {
      instance_id: instanceId,
      status: status.status,
    });
    void signalR.disconnect(instanceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.status, instanceId]); // signalR.disconnect is a stable module-level ref

  // Tick `now` for stale recalculation.
  useEffect(() => {
    if (disabled) return;
    const id = setInterval(() => {
      if (mountedRef.current) setNow(Date.now());
    }, 5_000);
    return () => clearInterval(id);
  }, [disabled]);

  const isStale = useMemo(() => {
    if (lastSuccessAt === null) return false;
    return now - lastSuccessAt > staleAfterMs;
  }, [now, lastSuccessAt, staleAfterMs]);

  return {
    status,
    currentPhase: status?.current_phase ?? null,
    // NB: no `phases` here. This hook used to return a PhaseStatus[] derived
    // from `status.phase_outputs` — a field nothing emits — so it was always
    // just the current phase, and no caller ever read it. Phase status comes
    // from `derivePhaseStatusesFromInstance`, which reads the real `phases`
    // history off the same status object.
    lastError,
    lastSuccessAt,
    isStale,
    isTerminal,
    loading,
    isLiveConnected: signalR.isConnected,
    isSignalRUnavailable: signalR.isSignalRUnavailable,
    refetch,
    subscribe: signalR.subscribe,
  };
}

export default useInstancePush;
